// Commercial record of a workshop (Plataforma → Talleres → Ficha comercial):
// plan, status, pilot dates, discounts with their end, payment status and
// notes. Kalfuntech's own data about its customer, never the workshop's: it
// lives in `fichasComerciales/{tallerId}` (outside the tenant tree), only the
// platform superadmin reads it (firestore.rules) and only the
// `guardarFichaComercial` callable writes it, validating with this schema and
// keeping a history in `fichasComerciales/{tallerId}/historial`.
// Rules: docs/comercial/plan-de-negocio.md §5.3 and §5.4 (approved on
// 2026-09-27). The launch offer is never called a "founder" price; its id is
// `lanzamiento`. Prices mirror apps/landing/src/data/comercial.ts (a landing
// test checks both agree).
import { z } from 'zod';
import { REGEX_CODIGO_CUPON } from './contacto.ts';
import { MAX_PORCENTAJE_CUPON } from './cupones.ts';

// ---- Plans and the business rules ----

/** Same ids as the monthly usage counter (PLANES_USO). */
export const PLANES_COMERCIALES = [
  'tallerza_uso',
  'tallerza_base',
  'tallerza_pro',
  'tallerza_volumen',
] as const;
export type PlanComercial = (typeof PLANES_COMERCIALES)[number];

export type InfoPlan = {
  nombre: string;
  /** Monthly price (CLP + IVA); Por uso: its monthly minimum; Alto volumen: "from". */
  precioMensual: number;
  /** Cars included per month; null in Por uso (it pays each car). */
  tope: number | null;
};

export const INFO_PLANES: Record<PlanComercial, InfoPlan> = {
  tallerza_uso: { nombre: 'Por uso', precioMensual: 19_800, tope: null },
  tallerza_base: { nombre: 'Taller', precioMensual: 49_990, tope: 60 },
  tallerza_pro: { nombre: 'Taller Pro', precioMensual: 89_990, tope: 150 },
  tallerza_volumen: { nombre: 'Alto volumen', precioMensual: 129_990, tope: 250 },
};

/** Por uso: price per car and the cars its monthly minimum covers. */
export const POR_USO = { precioPorAuto: 990, autosMinimo: 20 } as const;

export const REGLAS_COMERCIALES = {
  /** Paying the year up front (%, for 12 months). */
  descuentoAnual: 15,
  /** Launch offer: % off for `meses`, first `talleres` that pay. */
  lanzamiento: { descuento: 30, meses: 12, talleres: 10 },
  /** No bill gets more than this total discount (%). */
  topeDescuento: 35,
  /** Pilots running at the same time while the owner does the onboarding. */
  pilotosEnParalelo: 3,
} as const;

/** Plans the launch offer applies to (not Por uso). */
export const PLANES_LANZAMIENTO: readonly PlanComercial[] = [
  'tallerza_base',
  'tallerza_pro',
  'tallerza_volumen',
];
/** Plans a campaign coupon applies to. */
export const PLANES_CUPON: readonly PlanComercial[] = ['tallerza_base', 'tallerza_pro'];

/**
 * Free pilot (§5.4): its length, the joint review day and the day before
 * which the workshop says it will not continue. Conversion is the day after
 * the last one (day 61, or 31 in Por uso).
 */
export type ReglasPiloto = { dias: number; revision: number; aviso: number };
export const PILOTO: ReglasPiloto = { dias: 60, revision: 30, aviso: 55 };
export const PILOTO_POR_USO: ReglasPiloto = { dias: 30, revision: 15, aviso: 25 };
export const reglasPiloto = (plan: PlanComercial) =>
  plan === 'tallerza_uso' ? PILOTO_POR_USO : PILOTO;

export const ESTADOS_COMERCIALES = ['piloto', 'activo', 'suspendido', 'terminado'] as const;
export type EstadoComercial = (typeof ESTADOS_COMERCIALES)[number];
export const ETIQUETAS_ESTADO_COMERCIAL: Record<EstadoComercial, string> = {
  piloto: 'Piloto',
  activo: 'Activo',
  suspendido: 'Suspendido',
  terminado: 'Terminado',
};

export const ESTADOS_PAGO = ['al_dia', 'pendiente', 'atrasado'] as const;
export type EstadoPago = (typeof ESTADOS_PAGO)[number];
export const ETIQUETAS_ESTADO_PAGO: Record<EstadoPago, string> = {
  al_dia: 'Al día',
  pendiente: 'Pendiente',
  atrasado: 'Atrasado',
};

export const TIPOS_DESCUENTO = ['lanzamiento', 'cupon', 'anual'] as const;
export type TipoDescuento = (typeof TIPOS_DESCUENTO)[number];
export const ETIQUETAS_DESCUENTO: Record<TipoDescuento, string> = {
  lanzamiento: 'Oferta de lanzamiento',
  cupon: 'Cupón',
  anual: 'Pago anual',
};

export const MAX_NOTAS_FICHA = 2000;

// ---- Whole days ('AAAA-MM-DD', Chile) ----

const DIA_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day written as 'AAAA-MM-DD'. */
export function esDiaIso(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = DIA_ISO.exec(v);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === v;
}

const aUtc = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const deUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const DIA_MS = 86_400_000;

export const sumarDias = (iso: string, dias: number) => deUtc(aUtc(iso) + dias * DIA_MS);

/** Calendar months later; the day is clamped to the month's end (31-01 + 1 → 28-02). */
export function sumarMeses(iso: string, meses: number): string {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  const total = a * 12 + (m - 1) + meses;
  const anio = Math.floor(total / 12);
  const mes = total % 12;
  const ultimo = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
  return deUtc(Date.UTC(anio, mes, Math.min(d, ultimo)));
}

/** Whole days from `a` to `b` (negative when b is earlier). */
export const diasEntre = (a: string, b: string) => Math.round((aUtc(b) - aUtc(a)) / DIA_MS);

// ---- The record (schema shared by the callable and the form) ----

export const MENSAJES_FICHA = {
  plan: 'Elige un plan.',
  estado: 'Elige el estado del taller.',
  pago: 'Elige el estado de pago.',
  fecha: 'Usa una fecha válida.',
  pilotoInicio: 'Indica el día en que partió el piloto.',
  porcentajeCupon: `El cupón va de 1 a ${MAX_PORCENTAJE_CUPON} %.`,
  mesesCupon: 'El cupón dura de 1 a 12 meses.',
  codigoCupon: 'Usa de 3 a 20 letras, números o guiones.',
  lanzamientoYCupon:
    'La oferta de lanzamiento y un cupón no se combinan: el contrato lleva uno de los dos.',
  lanzamientoPlan: 'La oferta de lanzamiento es solo para Taller, Taller Pro y Alto volumen.',
  cuponPlan: 'Los cupones se aplican solo en Taller y Taller Pro.',
  lanzamientoSinCupo: `Con este taller se pasa de los ${REGLAS_COMERCIALES.lanzamiento.talleres} cupos de la oferta de lanzamiento (cuentan solo las ofertas vigentes). Se libera uno cuando un taller termina, se le quita la oferta o se le cumplen los ${REGLAS_COMERCIALES.lanzamiento.meses} meses.`,
  notas: `Las notas llegan hasta ${MAX_NOTAS_FICHA} caracteres.`,
} as const;

const MF = MENSAJES_FICHA;
const dia = z.string({ error: MF.fecha }).refine(esDiaIso, { error: MF.fecha });
const entero = (min: number, max: number, error: string) =>
  z.number({ error }).int({ error }).min(min, { error }).max(max, { error });

export const campoFichaComercial = {
  plan: z.enum(PLANES_COMERCIALES, { error: MF.plan }),
  estado: z.enum(ESTADOS_COMERCIALES, { error: MF.estado }),
  /** First day of the pilot (day 1). Required while `estado` is 'piloto'. */
  pilotoInicio: dia.optional(),
  /** Launch offer: 30 % for 12 months from `desde`. */
  lanzamiento: z.strictObject({ desde: dia }).optional(),
  /** A coupon (campaign or internal) agreed with this workshop. */
  cupon: z
    .strictObject({
      codigo: z
        .string({ error: MF.codigoCupon })
        .regex(REGEX_CODIGO_CUPON, { error: MF.codigoCupon })
        .optional(),
      porcentaje: entero(1, MAX_PORCENTAJE_CUPON, MF.porcentajeCupon),
      meses: entero(1, 12, MF.mesesCupon),
      desde: dia,
    })
    .optional(),
  /** Annual payment: 15 % for 12 months from `desde`. */
  anual: z.strictObject({ desde: dia }).optional(),
  pago: z.enum(ESTADOS_PAGO, { error: MF.pago }),
  notas: z.string({ error: MF.notas }).trim().max(MAX_NOTAS_FICHA, { error: MF.notas }),
};

type FichaBase = z.infer<z.ZodObject<typeof campoFichaComercial>>;

/**
 * Cross-field rules of §5.3 and §5.4: one acquisition discount per contract
 * (launch offer OR coupon), the launch offer only in Taller, Pro and Alto
 * volumen, coupons only in Taller and Pro, and a pilot needs its start. The
 * 35 % cap is not an error: the annual discount stacks with either, and the
 * bill is capped (see `descuentoTotal`).
 */
export function reglasFicha(f: FichaBase, ctx: z.RefinementCtx) {
  if (f.estado === 'piloto' && !f.pilotoInicio) {
    ctx.addIssue({ code: 'custom', message: MF.pilotoInicio, path: ['pilotoInicio'] });
  }
  if (f.lanzamiento && f.cupon) {
    ctx.addIssue({ code: 'custom', message: MF.lanzamientoYCupon, path: ['cupon'] });
  }
  if (f.lanzamiento && !PLANES_LANZAMIENTO.includes(f.plan)) {
    ctx.addIssue({ code: 'custom', message: MF.lanzamientoPlan, path: ['lanzamiento'] });
  }
  if (f.cupon && !PLANES_CUPON.includes(f.plan)) {
    ctx.addIssue({ code: 'custom', message: MF.cuponPlan, path: ['cupon'] });
  }
}

export const fichaComercial = z.strictObject(campoFichaComercial).superRefine(reglasFicha);
export type FichaComercial = z.infer<typeof fichaComercial>;

/** Minimal Firestore Timestamp shape (admin or web SDK). */
type Marca = { seconds: number; nanoseconds: number };

/** `fichasComerciales/{tallerId}` as stored (absent optional = not agreed). */
export type FichaComercialDoc<F extends Marca = Marca> = FichaComercial & {
  tallerId: string;
  actualizadoEn: F;
  /** uid of the superadmin. */
  actualizadoPor: string;
};

/** `fichasComerciales/{t}/historial/{id}`: one entry per save. */
export type CambioFicha<F extends Marca = Marca> = {
  en: F;
  por: string;
  /** Top-level fields that changed (all of them on the first save). */
  campos: string[];
  antes: FichaComercial | null;
  despues: FichaComercial;
};

// ---- Leaving the platform (baja) ----

/**
 * A workshop whose record is 'terminado' left the platform: total block from
 * that save on (decision of 2026-10-16). Every account of the workshop is
 * disabled, its devices are revoked, the rules deny it every read and write
 * and its callables reject it; its data stays, and only the platform
 * superadmin may export it. 'suspendido' is read-only (esSuspension);
 * 'piloto' and 'activo' work normally.
 */
export const esBaja = (estado: EstadoComercial | undefined): boolean => estado === 'terminado';

/** HttpsError details `motivo` of a call from a workshop that was given its baja. */
export const MOTIVO_TALLER_DE_BAJA = 'taller_de_baja';
export const MENSAJE_TALLER_DE_BAJA =
  'Tu taller está dado de baja en Tallerza: nadie del taller puede ingresar ni usarlo. Para reactivarlo, escríbenos a contacto@tallerza.cl.';

// ---- Suspension (read-only) ----

/**
 * A workshop whose record is 'suspendido' is read-only (decision of
 * 2026-10-17): its people and tablets keep signing in and see everything
 * (PDFs included), but nothing is written: no inspection, signature, photo,
 * board move, nor change of people, devices or settings. `talleres/{t}
 * .soloLectura` true, written in the same transaction as the record; any
 * other status lifts it ('terminado' keeps whatever it had: the baja blocks
 * everything anyway).
 */
export const esSuspension = (estado: EstadoComercial | undefined): boolean =>
  estado === 'suspendido';

/** HttpsError details `motivo` of a write attempted by a suspended workshop. */
export const MOTIVO_TALLER_SUSPENDIDO = 'taller_suspendido';
export const MENSAJE_TALLER_SUSPENDIDO =
  'Taller en solo lectura: puedes ver todo, pero no guardar cambios. Para reactivarlo escribe a contacto@tallerza.cl.';

/**
 * The only callables a suspended workshop may still use: signing in and
 * keeping its own session (PIN, password), reading, accepting the Terms
 * (without it the backoffice shows nothing) and revoking a PDF link. Every other callable writes and
 * is rejected with MOTIVO_TALLER_SUSPENDIDO. An explicit allow list: a new
 * callable is blocked until it is added here on purpose.
 */
export const CALLABLES_EN_SOLO_LECTURA = [
  'aceptarTerminos',
  'cambiarPassword',
  'exportarDatosTaller',
  'listarOperarios',
  'loginPin',
  'resumenBajaTaller',
  // Revoking a leaked PDF link is protective, never new content (enlaces.ts).
  'revocarEnlacePdf',
  // Same for an order's tracking page (seguimiento.ts).
  'revocarSeguimiento',
  'sincronizarClave',
  'solicitarRestablecerClave',
] as const;

/**
 * `talleres/{t}.baja`, written by `guardarFichaComercial` while the workshop
 * is dado de baja (`talleres/{t}.activo` false) and removed on reactivation.
 * It keeps what was working at the moment of the baja so that reactivating
 * restores exactly that, never an account an admin had already deactivated.
 */
export type BajaTaller<F = unknown> = {
  en: F;
  por: string;
  /** People (`usuarios`) whose account was active. */
  personas: string[];
  /** Shared device accounts (`equipos`) that were active. */
  equipos: string[];
  /** Every person of the workshop: their PIN shadow `pin_<uid>` is disabled too. */
  sombras: string[];
  /** Devices that were approved or pending, with that state. */
  dispositivos: { id: string; activo: boolean; pendiente: boolean }[];
};

/** What a save did to the workshop's access, when it changed it. */
export type CambioAccesoTaller = {
  accion: 'baja' | 'reactivacion';
  cuentas: number;
  dispositivos: number;
};

/** Answer of `resumenBajaTaller`: what a baja would block right now. */
export type ResumenBajaTaller = { dadoDeBaja: boolean; cuentas: number; dispositivos: number };

/**
 * Answer of `guardarFichaComercial`: non-blocking warnings (slots exceeded)
 * and, when the save gave the workshop its baja or reactivated it, how many
 * accounts and devices it blocked or restored; `lectura` when it suspended
 * the workshop or lifted its suspension.
 */
export type RespuestaGuardarFicha = {
  avisos: string[];
  acceso?: CambioAccesoTaller;
  /** When the save suspended the workshop (read-only) or lifted it. */
  lectura?: 'suspension' | 'reanudacion';
};

// ---- Derived values (backoffice and callable) ----

export type EstadoDescuento = 'por_partir' | 'vigente' | 'vencido';

export type DescuentoFicha = {
  tipo: TipoDescuento;
  porcentaje: number;
  desde: string;
  /** Last day with the discount (inclusive). */
  hasta: string;
  estado: EstadoDescuento;
  codigo?: string;
};

/** The record's discounts with their end and status on `hoy` ('AAAA-MM-DD'). */
export function descuentosFicha(
  f: Pick<FichaComercial, 'lanzamiento' | 'cupon' | 'anual'>,
  hoy: string,
): DescuentoFicha[] {
  const r: DescuentoFicha[] = [];
  const agregar = (
    tipo: TipoDescuento,
    porcentaje: number,
    desde: string,
    meses: number,
    codigo?: string,
  ) => {
    const hasta = sumarDias(sumarMeses(desde, meses), -1);
    const estado: EstadoDescuento =
      hoy < desde ? 'por_partir' : hoy > hasta ? 'vencido' : 'vigente';
    r.push({ tipo, porcentaje, desde, hasta, estado, ...(codigo ? { codigo } : {}) });
  };
  const L = REGLAS_COMERCIALES.lanzamiento;
  if (f.lanzamiento) agregar('lanzamiento', L.descuento, f.lanzamiento.desde, L.meses);
  if (f.cupon) agregar('cupon', f.cupon.porcentaje, f.cupon.desde, f.cupon.meses, f.cupon.codigo);
  if (f.anual) agregar('anual', REGLAS_COMERCIALES.descuentoAnual, f.anual.desde, 12);
  return r;
}

/**
 * Discount of a bill on `hoy`: the sum of the ones in force, capped at 35 %
 * of the list price (§5.3 rule 1). `topado` when the cap cut it.
 */
export function descuentoTotal(
  f: Pick<FichaComercial, 'lanzamiento' | 'cupon' | 'anual'>,
  hoy: string,
): { suma: number; aplicado: number; topado: boolean } {
  const suma = descuentosFicha(f, hoy)
    .filter((d) => d.estado === 'vigente')
    .reduce((s, d) => s + d.porcentaje, 0);
  const aplicado = Math.min(suma, REGLAS_COMERCIALES.topeDescuento);
  return { suma, aplicado, topado: aplicado < suma };
}

export type HitoPiloto = {
  id: 'revision' | 'aviso' | 'conversion';
  /** Day of the pilot (day 1 = start). */
  dia: number;
  fecha: string;
  /** 'pasado' | 'hoy' | 'proximo' (within 7 days) | 'pendiente'. */
  estado: 'pasado' | 'hoy' | 'proximo' | 'pendiente';
};

/**
 * The pilot on `hoy`: the current day (1 = start) and its milestones (joint
 * review, last day to say it will not continue, conversion). `aviso` is the
 * last day the notice is still on time (day 54: "before day 55").
 */
export function estadoPiloto(plan: PlanComercial, inicio: string, hoy: string) {
  const reglas = reglasPiloto(plan);
  const diaActual = diasEntre(inicio, hoy) + 1;
  const hito = (id: HitoPiloto['id'], d: number): HitoPiloto => {
    const fecha = sumarDias(inicio, d - 1);
    const falta = diasEntre(hoy, fecha);
    const estado =
      falta < 0 ? 'pasado' : falta === 0 ? 'hoy' : falta <= 7 ? 'proximo' : 'pendiente';
    return { id, dia: d, fecha, estado };
  };
  return {
    diaActual,
    dias: reglas.dias,
    /** Last free day (inclusive). */
    fin: sumarDias(inicio, reglas.dias - 1),
    hitos: [
      hito('revision', reglas.revision),
      hito('aviso', reglas.aviso - 1),
      hito('conversion', reglas.dias + 1),
    ],
  };
}

/** Minimal view of a stored record for the slot counts. */
export type FichaParaCupos = { tallerId: string; estado: EstadoComercial; lanzamiento?: unknown };

/**
 * Whether a record holds one of the launch offer's slots on `hoy`: only an
 * offer in force does. It has the offer, the workshop has not left (estado
 * 'terminado') and its 12 months are not over; one that has not started yet
 * already holds its slot, and so does one with a malformed start (never
 * frees a slot by mistake). A suspended workshop keeps it (it may come back).
 */
export function ocupaCupoLanzamiento(
  f: Pick<FichaParaCupos, 'estado' | 'lanzamiento'> | null | undefined,
  hoy: string,
): boolean {
  if (!f?.lanzamiento || f.estado === 'terminado') return false;
  const desde = (f.lanzamiento as { desde?: unknown }).desde;
  if (!esDiaIso(desde)) return true;
  return hoy <= sumarDias(sumarMeses(desde, REGLAS_COMERCIALES.lanzamiento.meses), -1);
}

/**
 * Saving `ficha` for `tallerId` takes a NEW launch slot (it did not hold one
 * in `otras`, it will now) while the other workshops already hold all of
 * them. Editing a record that already holds its slot never counts. Only a
 * warning: the 10 is not a hard cap (decision of 2026-09-28).
 */
export function excedeCupoLanzamiento(
  otras: readonly FichaParaCupos[],
  tallerId: string,
  ficha: Pick<FichaParaCupos, 'estado' | 'lanzamiento'>,
  hoy: string,
): boolean {
  const propia = otras.find((o) => o.tallerId === tallerId);
  if (!ocupaCupoLanzamiento(ficha, hoy) || ocupaCupoLanzamiento(propia, hoy)) return false;
  const ocupados = otras.filter(
    (o) => o.tallerId !== tallerId && ocupaCupoLanzamiento(o, hoy),
  ).length;
  return ocupados >= REGLAS_COMERCIALES.lanzamiento.talleres;
}

/**
 * Warnings (never errors) when saving `ficha` for `tallerId`: it goes over
 * the launch offer's 10 slots (only offers in force count) or over the 3
 * pilots at the same time.
 */
export function avisosCupos(
  otras: readonly FichaParaCupos[],
  tallerId: string,
  ficha: Pick<FichaParaCupos, 'estado' | 'lanzamiento'>,
  hoy: string,
): string[] {
  const resto = otras.filter((o) => o.tallerId !== tallerId);
  const avisos: string[] = [];
  if (excedeCupoLanzamiento(otras, tallerId, ficha, hoy)) avisos.push(MF.lanzamientoSinCupo);
  const cupoP = REGLAS_COMERCIALES.pilotosEnParalelo;
  const pilotos =
    resto.filter((o) => o.estado === 'piloto').length + (ficha.estado === 'piloto' ? 1 : 0);
  if (ficha.estado === 'piloto' && pilotos > cupoP) {
    avisos.push(
      `Con este taller hay ${pilotos} pilotos en paralelo; el máximo recomendado es ${cupoP}.`,
    );
  }
  return avisos;
}

/** JSON with sorted keys, so two equal objects always compare equal. */
function estable(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'undefined';
  if (Array.isArray(v)) return `[${v.map(estable).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${estable(o[k])}`)
    .join(',')}}`;
}

/** Top-level fields that differ between two records (for the history). */
export function camposCambiados(antes: FichaComercial | null, despues: FichaComercial): string[] {
  const claves = new Set([...Object.keys(antes ?? {}), ...Object.keys(despues)]);
  const a = (antes ?? {}) as Record<string, unknown>;
  const d = despues as Record<string, unknown>;
  return [...claves].filter((k) => estable(a[k]) !== estable(d[k])).sort();
}
